import * as React from 'react';
import { Form, List, Popup, Segment } from 'semantic-ui-react';

interface ItemEditableProps {
    /**
     * Elemento que se renderizará en el lugar del componente y se mostrara siempre que
     * el componente no esté en modo de edición.
     */
    elementoDeLectura: React.ReactNode;
    /**
     * Elemento que se renderizará en el lugar del componente cada vez que este entre
     * en modo de edición. El elemento dado se renderizará dentro de un Form de
     * semantic-ui-react. Si se omite, el componente será implícitamente de solo
     * lectura y no mostrará el icono para cambiar a modo edición.
     */
    elementoEditable?: React.ReactNode;
    /**
     * Si es `true` el componente será de solo lectura y no mostrará el icono para
     * editar un componente. Por defecto es `false`. Si es `false` el componente de
     * todos modos requiere el props `elementoEditable` para entrar en modo edición.
     */
    soloLectura?: boolean;
    /**
     * Callback que será llamado cuando el botón "editar" sea presionado.
     */
    alPresionarEditar?(): void;
    /**
     * Callback que será llamado cuando el botón "Guardar cambios", visible solo en
     * modo de edición, sea presionado. Si no se pasa una función o esta no tiene valor
     * de retorno (o es `undefined`) el componente saldrá inmediatamente del modo de
     * edición. Si la función retorna una promesa el componente esperará a que se
     * resuelva mostrando en tanto un spinner, presionar nuevamente el botón no tendrá
     * efecto alguno; si la promesa se resuelve al valor `true` o `undefined` el
     * componente saldrá del modo de edición, en cambio si la promesa se resuelve al
     * valor `false` el spinner se detendrá pero dejará el componente en modo de
     * edición. Si la promesa se rechaza el error no será propagado ni se efectuará
     * ninguna acción especial, efectivamente "tragándose" el error; el componente
     * quedará en modo de edición con el spinner ejecutándose.
     */
    alPresionarGuardar?(): Promise<boolean | void> | void;
    /**
     * Callback que será llamado cuando el botón "Cancelar", visible solo en modo de
     * edición, sea presionado. Una vez la función retorne, o si no se provee una, el
     * componente saldrá del modo de edición.
     */
    alPresionarCancelar?(): void;
}

interface State {
    editando: boolean;
    trabajando: boolean;
}

export class ItemEditable extends React.Component<ItemEditableProps, State> {
    public state: State = {
        editando: false,
        trabajando: false,
    };

    public render() {
        const { elementoDeLectura, elementoEditable, soloLectura } = this.props;
        const { editando, trabajando } = this.state;

        const esEditable = !soloLectura && !!elementoEditable;

        return (
            <List.Item>
                {!editando || !elementoEditable ? (
                    <React.Fragment>
                        {esEditable && (
                            <List.Content floated='right'>
                                <Popup
                                    inverted
                                    trigger={
                                        <List.Icon
                                            link
                                            color='blue'
                                            name='edit'
                                            onClick={this.volverEditable}
                                        />
                                    }
                                    content='Editar'
                                    position='top center'
                                />
                            </List.Content>
                        )}
                        <List.Content content={elementoDeLectura} />
                    </React.Fragment>
                ) : (
                    <Segment raised>
                        <Form onSubmit={this.manejarGuardarCambios}>
                            {elementoEditable}
                            <Form.Group>
                                <Form.Button
                                    primary
                                    loading={trabajando}
                                    content='Guardar cambios'
                                />
                                <Form.Button
                                    content='Cancelar'
                                    type='button'
                                    disabled={trabajando}
                                    onClick={this.manejarCancelar}
                                />
                            </Form.Group>
                        </Form>
                    </Segment>
                )}
            </List.Item>
        );
    }

    private readonly manejarGuardarCambios = () => {
        if (this.state.trabajando) {
            return;
        }

        const { alPresionarGuardar } = this.props;

        if (!alPresionarGuardar) {
            // si no se pasó el callback, solo cerrar y salir
            this.volverNoEditable();
            return;
        }

        this.setState({ trabajando: true });

        const retorno = alPresionarGuardar();
        if (retorno === undefined) {
            // se pasó el callback pero no retornó nada, cerrar y salir
            this.volverNoEditable();
            return;
        }

        // la única opción restante es que el retorno del callback sea una promesa
        retorno
            .then(respuesta => {
                if (respuesta === false) {
                    this.setState({ trabajando: false });
                } else {
                    this.volverNoEditable();
                }
            })
            .catch(() => undefined);
    };

    private readonly manejarCancelar = () => {
        const { alPresionarCancelar } = this.props;

        if (alPresionarCancelar) {
            alPresionarCancelar();
        }

        this.volverNoEditable();
    };

    private readonly volverEditable = () => {
        this.setState({ editando: true, trabajando: false });
        if (this.props.alPresionarEditar) this.props.alPresionarEditar();
    };

    private readonly volverNoEditable = () => {
        this.setState({ editando: false, trabajando: false });
    };
}

export default ItemEditable;



// WEBPACK FOOTER //
// ./src/componentes/ItemEditable/index.tsx